import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Chrome, Clapperboard } from "lucide-react";
import { login, register, forgotPassword } from "../lib/api";
import { useAuth } from "../auth";

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const Field = ({ testid, ...props }) => (
  <input
    data-testid={testid}
    {...props}
    className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-white placeholder-zinc-500 focus:outline-none focus:border-white/30 focus:bg-white/10 transition-colors duration-300 font-ui text-sm"
  />
);

export default function Auth() {
  const { user, setUser } = useAuth();
  const [mode, setMode] = useState("login"); // login | signup | forgot
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        await forgotPassword(form.email);
        setSent(true);
      } else {
        const u =
          mode === "login"
            ? await login({ email: form.email, password: form.password })
            : await register(form);
        setUser(u);
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const switchMode = (m) => {
    setMode(m);
    setError("");
    setSent(false);
  };

  const titles = {
    login: ["Welcome back.", "Sign in to your screening room"],
    signup: ["Take a seat.", "Create your private screening room"],
    forgot: ["Lost your ticket?", "We'll email you a reset link"],
  };

  return (
    <div data-testid="auth-page" className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
      <motion.div
        className="absolute inset-0"
        style={{ background: "radial-gradient(circle at center, #18181b 0%, #000000 70%)" }}
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="flex items-center gap-3 justify-center mb-10">
          <Clapperboard className="w-5 h-5 text-white" strokeWidth={1.5} />
          <span className="font-meta text-xs tracking-[0.4em] uppercase text-white">PurePlay</span>
        </div>
        <div className="bg-[#050505] border border-white/10 rounded-2xl p-8 md:p-10">
          <h1 className="font-heading text-3xl text-white tracking-tight">{titles[mode][0]}</h1>
          <p className="mt-2 font-meta text-[11px] uppercase tracking-[0.25em] text-zinc-500">{titles[mode][1]}</p>
          <form onSubmit={submit} className="mt-8 flex flex-col gap-4">
            {mode === "signup" && (
              <div className="grid grid-cols-2 gap-4">
                <Field testid="signup-first-name-input" placeholder="First name" value={form.first_name} onChange={set("first_name")} required />
                <Field testid="signup-last-name-input" placeholder="Last name" value={form.last_name} onChange={set("last_name")} required />
              </div>
            )}
            <Field
              testid={mode === "login" ? "login-email-input" : mode === "signup" ? "signup-email-input" : "forgot-email-input"}
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={set("email")}
              required
            />
            {mode !== "forgot" && (
              <Field
                testid={mode === "login" ? "login-password-input" : "signup-password-input"}
                type="password"
                placeholder="Password"
                value={form.password}
                onChange={set("password")}
                required
                minLength={6}
              />
            )}
            {mode === "login" && (
              <button
                type="button"
                data-testid="forgot-password-link"
                onClick={() => switchMode("forgot")}
                className="self-end font-meta text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors duration-200"
              >
                Forgot password?
              </button>
            )}
            {error && (
              <p data-testid="auth-error" className="text-red-400 text-sm">
                {error}
              </p>
            )}
            {sent && (
              <p data-testid="forgot-sent" className="text-emerald-400 text-sm">
                If that email has an account, a reset link is on its way.
              </p>
            )}
            <button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading}
              className="mt-2 w-full bg-white text-black rounded-full py-3.5 font-meta text-xs uppercase tracking-[0.25em] hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200 disabled:opacity-50"
            >
              {loading ? "One moment…" : mode === "login" ? "Sign In" : mode === "signup" ? "Create Account" : "Send Reset Link"}
            </button>
          </form>
          {mode !== "forgot" && (
            <>
              <div className="mt-6 flex items-center gap-4">
                <div className="flex-1 h-px bg-white/10" />
                <span className="font-meta text-[10px] uppercase tracking-[0.25em] text-zinc-600">or</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <button
                data-testid="google-login-button"
                onClick={googleLogin}
                className="mt-6 w-full border border-white/15 rounded-full py-3.5 flex items-center justify-center gap-3 text-zinc-300 hover:text-white hover:border-white/40 transition-colors duration-200 font-meta text-xs uppercase tracking-[0.25em]"
              >
                <Chrome className="w-4 h-4" />
                Continue with Google
              </button>
            </>
          )}
        </div>
        <p className="mt-6 text-center text-sm text-zinc-500">
          {mode === "forgot" ? (
            <button
              data-testid="auth-toggle"
              onClick={() => switchMode("login")}
              className="text-white underline underline-offset-4 hover:text-zinc-300 transition-colors duration-200"
            >
              Back to sign in
            </button>
          ) : (
            <>
              {mode === "login" ? "No account yet?" : "Already have a seat?"}{" "}
              <button
                data-testid="auth-toggle"
                onClick={() => switchMode(mode === "login" ? "signup" : "login")}
                className="text-white underline underline-offset-4 hover:text-zinc-300 transition-colors duration-200"
              >
                {mode === "login" ? "Create one" : "Sign in"}
              </button>
            </>
          )}
        </p>
      </motion.div>
    </div>
  );
}
